// Content for the ten Home Service pages (Template B).
// Unique copy per page, local Lowcountry tone, no vetting/licensing/insurance claims.

export const HOME_SERVICES = [
  {
    slug: "hvac",
    name: "HVAC",
    kind: "service",
    h1: "HVAC Specialist Connections in the SC Lowcountry",
    metaTitle: "HVAC Specialist Connections in SC | Home Pro Connect",
    metaDescription: "AC trouble in the Lowcountry heat? Home Pro Connect connects you with an HVAC specialist for repair, replacement, and maintenance. One request starts it.",
    intro: [
      "In the Lowcountry, air conditioning isn't a luxury — it's survival from May through October. When your system struggles against the Charleston humidity or quits on the hottest week of the year, you need help fast.",
      "Home Pro Connect connects you with an HVAC specialist from our network for repairs, replacements, and seasonal maintenance — starting with one simple request.",
    ],
    includesTitle: "Common HVAC jobs we can connect you with",
    includes: [
      "AC repair and troubleshooting for systems that blow warm or weak air",
      "Full system replacement — condensers, air handlers, and heat pumps",
      "Seasonal tune-ups before summer and winter",
      "Ductwork inspection, sealing, and replacement",
      "Thermostat upgrades, including smart thermostats",
      "Indoor air quality add-ons like dehumidifiers and better filtration",
      "Ductless mini-split installation for additions and bonus rooms",
    ],
    stepsTitle: "How we connect you with an HVAC specialist",
    considerations: [
      "Repair or replace: age, repair cost, and refrigerant type all factor in. A specialist can walk you through the math for your specific system.",
      "Sizing matters: an oversized unit short-cycles and leaves humidity behind; undersized runs constantly. Proper sizing is based on your home, not a guess.",
      "Humidity control: in our climate, managing moisture is as important as temperature. Ask about whole-home dehumidification if your house feels sticky.",
      "Timing: replacements are easiest to schedule before peak summer. If your system is aging, spring is the smart season to plan.",
    ],
    faqs: [
      { q: "How often should my HVAC system be serviced?", a: "Most manufacturers recommend professional service twice a year — once before cooling season and once before heating season. Regular tune-ups catch small issues before they become breakdowns." },
      { q: "What are signs my AC is failing?", a: "Watch for warm air, weak airflow, unusual noises, frequent cycling, rising power bills, and moisture around the unit. Any of these is worth a service call." },
      { q: "How long does an AC replacement take?", a: "A straightforward like-for-like replacement is often completed in a single day. Jobs involving new ductwork or relocation take longer — your specialist will outline the schedule." },
      { q: "Is a ductless mini-split right for my addition or garage?", a: "Mini-splits are a popular Lowcountry solution for spaces without ductwork. A specialist can confirm sizing and placement for your room." },
    ],
    relatedServices: ["plumbing", "drywall", "painting"],
    relatedProjects: ["complete-home-remodels", "home-additions"],
    guideTags: ["Hiring Tips"],
  },
  {
    slug: "flooring",
    name: "Flooring",
    kind: "service",
    h1: "Flooring Specialist Connections in the SC Lowcountry",
    metaTitle: "Flooring Specialist Connections in SC | Home Pro Connect",
    metaDescription: "New floors for your Lowcountry home? Home Pro Connect connects you with a flooring specialist for installation, refinishing, and repair. Start with one request.",
    intro: [
      "Floors take everything your household throws at them — sand tracked in from the beach, humidity swings, pets, kids, and everyday life. When it's time to replace worn carpet or tired tile, the right material and a careful installation make all the difference.",
      "Home Pro Connect connects you with a flooring specialist from our network who can help you choose and install floors built for Lowcountry living.",
    ],
    includesTitle: "Common flooring jobs we can connect you with",
    includes: [
      "Luxury vinyl plank and vinyl tile installation",
      "Hardwood installation, refinishing, and repair",
      "Tile flooring for kitchens, baths, and laundry rooms",
      "Carpet replacement for bedrooms and living areas",
      "Subfloor repair and leveling before installation",
      "Baseboard and trim reinstallation after new floors",
      "Moisture barrier installation over crawlspace subfloors",
    ],
    stepsTitle: "How we connect you with a flooring specialist",
    considerations: [
      "Humidity and moisture: coastal air is hard on some materials. Luxury vinyl plank and tile handle moisture well; solid hardwood needs proper acclimation before installation.",
      "Subfloor condition: squeaks, soft spots, and uneven areas should be addressed before new flooring goes down — a good installer inspects first.",
      "Transitions and trim: ask how thresholds, stairs, and baseboards will be handled so the finished look is clean.",
      "Furniture logistics: clarify who moves furniture and appliances, and whether old flooring removal and disposal are included in the estimate.",
    ],
    faqs: [
      { q: "What flooring holds up best in Lowcountry humidity?", a: "Luxury vinyl plank and porcelain tile are local favorites because they handle moisture and temperature swings. Engineered hardwood is another solid option when properly installed." },
      { q: "Can new flooring go over my existing floor?", a: "Sometimes — floating floors like LVP can go over flat, sound surfaces. Tile and damaged floors usually need removal first. Your installer will assess during the estimate." },
      { q: "How long does flooring installation take?", a: "A single room can often be done in a day; whole-house projects typically run several days, plus acclimation time for wood products." },
      { q: "Do I need to move all my furniture?", a: "Installers work room by room, and many will move standard furniture as part of the job. Confirm what's included — pianos, appliances, and fragile items are usually on you." },
    ],
    relatedServices: ["painting", "drywall", "plumbing"],
    relatedProjects: ["kitchen-and-bath-remodels", "complete-home-remodels"],
    guideTags: ["Hiring Tips"],
  },
  {
    slug: "plumbing",
    name: "Plumbing",
    kind: "service",
    h1: "Plumbing Specialist Connections in the SC Lowcountry",
    metaTitle: "Plumbing Specialist Connections in SC | Home Pro Connect",
    metaDescription: "Leak, clog, or water heater trouble in the Lowcountry? Home Pro Connect connects you with a plumbing specialist for repairs and installs. One request starts it.",
    intro: [
      "A dripping faucet is annoying. A hidden leak behind a wall is expensive. Plumbing problems rarely fix themselves — and in our humid climate, moisture that lingers turns into bigger repairs.",
      "Home Pro Connect connects you with a plumbing specialist from our network for everything from fixture swaps to whole-home repipes, starting with one request.",
    ],
    includesTitle: "Common plumbing jobs we can connect you with",
    includes: [
      "Leak detection and repair for pipes, fixtures, and fittings",
      "Water heater repair, replacement, and tankless upgrades",
      "Drain clearing for sinks, tubs, showers, and main lines",
      "Faucet, toilet, and fixture installation",
      "Repiping older homes with aging supply lines",
      "Garbage disposal and dishwasher hookups",
      "Plumbing rough-in and trim-out for remodels and additions",
    ],
    stepsTitle: "How we connect you with a plumbing specialist",
    considerations: [
      "Small leaks, big damage: a slow drip inside a wall can damage framing and invite mold long before you see a stain. Investigate early.",
      "Know your shutoff: every homeowner should know where the main water shutoff is. If you don't, ask your plumber to show you on the first visit.",
      "Water heater age: tank water heaters commonly last around a decade. If yours is approaching that age, planning a replacement beats an emergency.",
      "Older pipes: many established Lowcountry neighborhoods have homes with aging galvanized or polybutylene lines. A plumber can tell you what you have and what it means.",
    ],
    faqs: [
      { q: "What are the signs of a hidden plumbing leak?", a: "Unexplained increases in your water bill, warm spots on floors, musty smells, and the sound of running water when everything is off all point to a hidden leak." },
      { q: "Tankless or tank water heater — which is better?", a: "Tankless units save space and heat water on demand; tank models cost less upfront. Your plumber can compare capacity, cost, and fit for your household." },
      { q: "When should a home be repiped?", a: "Frequent leaks, discolored water, low pressure, and aging pipe materials are the usual triggers. A repipe replaces supply lines throughout the home." },
      { q: "Can a plumber handle fixtures for my bathroom remodel?", a: "Yes — plumbers routinely set tubs, showers, vanities, and toilets as part of remodels, and coordinate rough-in work before walls close up." },
    ],
    relatedServices: ["drywall", "flooring", "hvac"],
    relatedProjects: ["kitchen-and-bath-remodels", "complete-home-remodels"],
    guideTags: ["Hiring Tips"],
  },
  {
    slug: "painting",
    name: "Painting",
    kind: "service",
    h1: "Painting Specialist Connections in the SC Lowcountry",
    metaTitle: "Painting Specialist Connections in SC | Home Pro Connect",
    metaDescription: "Interior or exterior painting in the SC Lowcountry? Home Pro Connect connects you with a painting specialist for your home. Start with one simple request.",
    intro: [
      "Few updates transform a home like fresh paint. Inside, it resets every room. Outside, it's your home's first defense against coastal sun, salt air, and summer storms.",
      "Home Pro Connect connects you with a painting specialist from our network for interiors, exteriors, and everything in between — starting with one simple request.",
    ],
    includesTitle: "Common painting jobs we can connect you with",
    includes: [
      "Full interior repaints — walls, ceilings, trim, and doors",
      "Exterior painting for siding, stucco, and brick",
      "Cabinet painting and refinishing for kitchens and baths",
      "Drywall repair and texture before painting",
      "Deck, fence, and porch staining or painting",
      "Color consultation and sample testing",
      "Mildew-resistant coatings for humid areas",
    ],
    stepsTitle: "How we connect you with a painting specialist",
    considerations: [
      "Prep is everything: the difference between paint that lasts two years and ten is surface preparation — cleaning, scraping, sanding, caulking, and priming.",
      "Coastal exposure: exteriors near the water face salt and intense sun. Ask about coatings rated for coastal conditions.",
      "Timing exterior work: dry, mild stretches of weather are best. Spring and fall are prime exterior seasons in the Lowcountry.",
      "What's included: clarify whether the estimate covers moving furniture, nail-hole filling, and full cleanup — details that separate a smooth job from a stressful one.",
    ],
    faqs: [
      { q: "How often should I repaint my home's exterior near the coast?", a: "Coastal homes typically need repainting more often than inland homes — sun, salt, and storms wear finishes faster. Your painter can assess your current coating and recommend timing." },
      { q: "How long does it take to paint a house interior?", a: "A few rooms may take a couple of days; a full interior usually runs about a week depending on prep, colors, and trim detail." },
      { q: "Do painters move furniture?", a: "Most will move and cover standard furniture as part of prep, and return rooms to order afterward. Confirm the specifics when you review the estimate." },
      { q: "Can cabinets be painted instead of replaced?", a: "Yes — professionally painted cabinets are a popular kitchen refresh at a fraction of replacement cost. Proper prep and durable enamel finishes are the keys." },
    ],
    relatedServices: ["drywall", "flooring", "fences"],
    relatedProjects: ["kitchen-and-bath-remodels", "complete-home-remodels"],
    guideTags: ["Hiring Tips"],
  },
  {
    slug: "drywall",
    name: "Drywall",
    kind: "service",
    h1: "Drywall Specialist Connections in the SC Lowcountry",
    metaTitle: "Drywall Specialist Connections in SC | Home Pro Connect",
    metaDescription: "Need drywall repair or installation in the SC Lowcountry? Home Pro Connect connects you with a drywall specialist for patches, texture, and new walls.",
    intro: [
      "Drywall damage has a way of staring at you — the doorknob hole, the water stain from an old leak, the crack that keeps coming back. A skilled finisher makes it disappear like it never happened.",
      "Home Pro Connect connects you with a drywall specialist from our network for repairs, texture matching, and new installation across the Lowcountry.",
    ],
    includesTitle: "Common drywall jobs we can connect you with",
    includes: [
      "Patch and repair for holes, cracks, and dents",
      "Water-damaged drywall removal and replacement",
      "Texture matching — smooth, orange peel, knockdown, and more",
      "New drywall hanging and finishing for additions and remodels",
      "Ceiling repair, including after plumbing or roof leaks",
      "Corner bead, joint, and seam repairs",
      "Primer and paint-ready finishing",
    ],
    stepsTitle: "How we connect you with a drywall specialist",
    considerations: [
      "Fix the cause first: water-damaged drywall means there was moisture — make sure the leak or condensation source is resolved before the repair.",
      "Texture matching is a craft: the mark of a great finisher is a patch you can't find. Ask how they approach matching your existing texture.",
      "Dust control: sanding creates fine dust that travels. Ask about containment and cleanup, especially in furnished rooms.",
      "Timing: joint compound needs drying time between coats, and humid Lowcountry air can extend that. Most repairs wrap in one to three visits.",
    ],
    faqs: [
      { q: "Can a drywall patch match my existing texture?", a: "Yes — experienced finishers replicate orange peel, knockdown, and other common textures so repairs blend in. Smooth-wall homes are matched with a fine skim coat." },
      { q: "Should water-damaged drywall always be replaced?", a: "If drywall is soft, swollen, or stained from an active leak, replacement is the reliable fix. First confirm the moisture source is fixed so the problem doesn't return." },
      { q: "How long does a typical drywall repair take?", a: "Small patches often finish in one visit; larger repairs need two or three because each coat of compound must dry. Humidity can add a day." },
      { q: "Do drywall specialists also paint?", a: "Many do finish work through primer and paint-ready surfaces, and some offer painting too. Ask about the full scope when you request your connection." },
    ],
    relatedServices: ["painting", "plumbing", "flooring"],
    relatedProjects: ["complete-home-remodels", "home-additions"],
    guideTags: ["Hiring Tips"],
  },
  {
    slug: "fences",
    name: "Fences",
    kind: "service",
    h1: "Fence Specialist Connections in the SC Lowcountry",
    metaTitle: "Fence Specialist Connections in SC | Home Pro Connect",
    metaDescription: "Need a fence in the SC Lowcountry? Home Pro Connect connects you with a fence specialist for wood, vinyl, and aluminum installation and repair.",
    intro: [
      "A good fence does a lot at once — privacy for your backyard, a safe space for kids and dogs, a clean property line, and curb appeal from the street. Coastal weather makes quality materials and solid posts essential.",
      "Home Pro Connect connects you with a fence specialist from our network for new installation and repair across the Lowcountry and Grand Strand.",
    ],
    includesTitle: "Common fence jobs we can connect you with",
    includes: [
      "Wood privacy fence installation and repair",
      "Vinyl and aluminum fencing for low-maintenance yards",
      "Pool enclosures and code-compliant pool barriers",
      "Gates — walk gates, drive gates, and hardware upgrades",
      "Post replacement and storm damage repair",
      "Dog runs, garden fencing, and decorative accents",
      "Old fence tear-out and haul-away",
    ],
    stepsTitle: "How we connect you with a fence specialist",
    considerations: [
      "Property lines first: fences belong on your side of the line. If boundaries are unclear, a survey is worth it before posts go in.",
      "HOA and permits: many Lowcountry neighborhoods regulate fence height, style, and placement — and some municipalities require permits. Check before you build.",
      "Material for the climate: pressure-treated pine is the classic; vinyl and aluminum shrug off humidity and salt air. Each has a look and upkeep profile worth comparing.",
      "Wind matters: storm season is real here. Post depth, spacing, and hardware determine how a fence rides out high winds.",
    ],
    faqs: [
      { q: "Do I need a permit or HOA approval for a fence?", a: "Often yes for one or both. Height limits, materials, and setbacks vary by neighborhood and municipality. Your installer can usually point you to the right office." },
      { q: "What fence material lasts longest near the coast?", a: "Vinyl and aluminum are the low-maintenance leaders in humid, salty air. Properly sealed wood offers the classic look with more upkeep." },
      { q: "How long does fence installation take?", a: "Most residential fences install in one to three days once materials are on site, depending on length, terrain, and gates." },
      { q: "Can a storm-damaged fence be repaired instead of replaced?", a: "Frequently — leaning posts and missing sections can be rebuilt in place. Widespread rot or failed posts throughout usually tips the decision toward replacement." },
    ],
    relatedServices: ["landscaping-and-lawn-maintenance", "painting", "tree-removal-and-land-clearing"],
    relatedProjects: ["home-building", "home-additions"],
    guideTags: ["Hiring Tips"],
  },
  {
    slug: "roofing",
    name: "Roofing",
    kind: "service",
    h1: "Roofing Specialist Connections in the SC Lowcountry",
    metaTitle: "Roofing Specialist Connections in SC | Home Pro Connect",
    metaDescription: "Roof repair or replacement in the SC Lowcountry? Home Pro Connect connects you with a roofing specialist for inspections, repairs, and new roofs.",
    intro: [
      "Your roof takes the brunt of Lowcountry life — summer sun, heavy rain, and the occasional tropical system rolling through. Small issues become big leaks fast, so it pays to act early.",
      "Home Pro Connect connects you with a roofing specialist from our network for inspections, repairs, and full replacements across the Lowcountry and Grand Strand.",
    ],
    includesTitle: "Common roofing jobs we can connect you with",
    includes: [
      "Roof inspections after storms or before buying or selling",
      "Leak diagnosis and repair — flashing, vents, valleys, and shingles",
      "Full roof replacement in asphalt shingle or metal",
      "Storm and wind damage assessment",
      "Skylight and chimney flashing repair",
      "Gutter and drip edge coordination with roof work",
      "Ventilation improvements to extend roof life",
    ],
    stepsTitle: "How we connect you with a roofing specialist",
    considerations: [
      "Repair vs. replace: age and damage extent decide it. A roof near the end of its life with recurring leaks is often smarter to replace than to keep patching.",
      "After a storm: document visible damage with photos and schedule an inspection promptly. If you file an insurance claim, your roofer's documentation helps.",
      "Material choices: architectural shingles are the local standard; metal roofing is popular for longevity and storm performance. Compare warranties and appearance.",
      "Ventilation: attics that breathe properly keep roofs healthier and cooling bills lower — worth discussing during any roof project.",
    ],
    faqs: [
      { q: "How long does a roof last in coastal South Carolina?", a: "Sun, salt air, and storms shorten roof life compared to milder climates. Regular inspections after major weather help you catch issues early regardless of age." },
      { q: "What are the signs I need roof repair?", a: "Missing or curling shingles, granules in gutters, water stains on ceilings, and daylight visible in the attic all deserve a professional look." },
      { q: "How long does a roof replacement take?", a: "Most single-family roof replacements complete in one to three days, weather permitting, with larger or complex roofs taking longer." },
      { q: "Will a roofer help with an insurance claim?", a: "Roofing specialists routinely document storm damage and provide the estimates insurers ask for. The claim itself is between you and your insurance company." },
    ],
    relatedServices: ["tree-removal-and-land-clearing", "drywall", "hvac"],
    relatedProjects: ["complete-home-remodels", "demolition"],
    guideTags: ["Hiring Tips"],
  },
  {
    slug: "landscaping-and-lawn-maintenance",
    name: "Landscaping & Lawn Maintenance",
    kind: "service",
    h1: "Landscaping & Lawn Care Connections in the SC Lowcountry",
    metaTitle: "Landscaping & Lawn Care Connections in SC | HPC",
    metaDescription: "Want a yard that turns heads in the Lowcountry? Home Pro Connect connects you with landscaping and lawn maintenance specialists. One request starts it.",
    intro: [
      "Lowcountry lawns grow fast and gardens grow wild — between the long growing season and summer storms, keeping up takes real effort. A reliable crew turns your yard from a chore into the best-looking one on the street.",
      "Home Pro Connect connects you with a landscaping and lawn maintenance specialist from our network for ongoing care or one-time transformations.",
    ],
    includesTitle: "Common landscaping jobs we can connect you with",
    includes: [
      "Weekly and bi-weekly mowing, edging, and blowing",
      "Seasonal cleanups — leaves, pine needles, and storm debris",
      "Mulch and pine straw installation for beds",
      "Shrub and hedge trimming and shaping",
      "Sod installation and lawn renovation",
      "Flower bed design and seasonal planting",
      "Irrigation startup, repair, and adjustments",
    ],
    stepsTitle: "How we connect you with a landscaping specialist",
    considerations: [
      "Consistency is the secret: lawns look best on a regular schedule. Decide whether you want weekly or bi-weekly service before you request quotes.",
      "Pine straw or mulch: both are Lowcountry staples. Pine straw suits natural, woodsy beds; mulch offers a manicured look. Your crew can refresh either seasonally.",
      "Palmetto-friendly choices: native and coastal-tough plants handle our heat, salt, and storms better than finicky imports — ask for suggestions.",
      "Storm season prep: late-summer cleanups and drainage checks help yards ride out heavy rain with less damage.",
    ],
    faqs: [
      { q: "How often should my lawn be serviced in the growing season?", a: "Most Lowcountry lawns need weekly mowing from spring through early fall, scaling back to bi-weekly or monthly visits in the cooler months." },
      { q: "What's typically included in lawn maintenance?", a: "Standard visits cover mowing, edging along walks and beds, string trimming, and blowing off hard surfaces. Bed work, mulch, and cleanups are usually quoted separately." },
      { q: "Can a lawn crew handle a one-time cleanup?", a: "Absolutely — one-time cleanups are common before selling a home, after storms, or when a yard has gotten away from you." },
      { q: "Do landscapers handle irrigation problems?", a: "Many do repairs and adjustments for existing systems. Describe any irrigation issues in your request so the right specialist responds." },
    ],
    relatedServices: ["tree-removal-and-land-clearing", "fences", "trash-removal-and-move-out"],
    relatedProjects: ["home-building", "home-additions"],
    guideTags: ["Hiring Tips"],
  },
  {
    slug: "tree-removal-and-land-clearing",
    name: "Tree Removal & Land Clearing",
    kind: "service",
    h1: "Tree Removal & Land Clearing Connections in the SC Lowcountry",
    metaTitle: "Tree Removal & Land Clearing in SC | Home Pro Connect",
    metaDescription: "Hazardous tree or overgrown lot in the Lowcountry? Home Pro Connect connects you with tree removal and land clearing specialists. Start with one request.",
    intro: [
      "A leaning pine over the roofline or a storm-split oak isn't just an eyesore — it's a worry every time the wind picks up. And when you're preparing a lot to build, clearing it properly is step one.",
      "Home Pro Connect connects you with tree removal and land clearing specialists from our network across the Lowcountry and Grand Strand.",
    ],
    includesTitle: "Common tree and land clearing jobs we can connect you with",
    includes: [
      "Hazardous and dead tree removal near homes and structures",
      "Storm-damaged tree cleanup and removal",
      "Tree trimming and canopy thinning away from rooflines",
      "Stump grinding and removal",
      "Lot and land clearing for new construction",
      "Underbrush, brush, and overgrowth clearing",
      "Land management — forestry mulching and ongoing property maintenance",
    ],
    stepsTitle: "How we connect you with a tree removal specialist",
    considerations: [
      "Protected trees: some Lowcountry municipalities protect certain trees — notably grand oaks above specific sizes — and removal can require approval. Check local rules first.",
      "Proximity matters: trees near homes, pools, fences, or power lines require careful rigging and sometimes specialized equipment. Describe the location honestly in your request.",
      "Stumps: confirm whether stump grinding is included or priced separately so the finished yard matches your expectations.",
      "After storms: demand spikes following severe weather. Photos help specialists prioritize and quote faster.",
    ],
    faqs: [
      { q: "Do I need a permit to remove a tree in the Lowcountry?", a: "Sometimes. Several local municipalities regulate removal of larger or protected trees, especially live oaks. Your specialist or local planning office can confirm the rules for your address." },
      { q: "Is stump removal included with tree removal?", a: "It varies — some quotes include stump grinding, others price it separately. Always ask so you're not left with a stump in the yard." },
      { q: "What's the difference between land clearing and forestry mulching?", a: "Traditional clearing removes vegetation and hauls it away; forestry mulching grinds it in place into mulch that stays on the ground. Mulching is fast and leaves soil protected — great for land management." },
      { q: "Can you remove a tree that's leaning toward my house?", a: "Yes — that's exactly the kind of job tree specialists handle daily, using sectional removal and rigging to bring it down safely. Don't wait on a hazardous tree." },
    ],
    relatedServices: ["landscaping-and-lawn-maintenance", "trash-removal-and-move-out", "fences"],
    relatedProjects: ["home-building", "demolition"],
    guideTags: ["Hiring Tips"],
  },
  {
    slug: "trash-removal-and-move-out",
    name: "Trash Removal & Move-Out",
    kind: "service",
    h1: "Trash Removal & Move-Out Connections in the SC Lowcountry",
    metaTitle: "Trash Removal & Move-Out Help in SC | Home Pro Connect",
    metaDescription: "Junk removal, cleanouts, or move-out help in the Lowcountry? Home Pro Connect connects you with hauling specialists. One simple request starts it.",
    intro: [
      "Whether you're clearing out a garage in Ladson, handling a move-out in Charleston, or emptying a rental between tenants, hauling it all yourself takes a truck, time, and a strong back.",
      "Home Pro Connect connects you with trash removal and cleanout specialists from our network who handle the heavy lifting and responsible disposal.",
    ],
    includesTitle: "Common trash removal and move-out jobs we can connect you with",
    includes: [
      "Full-home and garage cleanouts",
      "Move-out junk removal for renters, sellers, and landlords",
      "Rental property turnover cleanups",
      "Furniture, appliance, and mattress removal",
      "Yard debris and storm cleanup hauling",
      "Estate cleanouts handled with care",
      "Donation and recycling drop-offs when items qualify",
    ],
    stepsTitle: "How we connect you with a trash removal specialist",
    considerations: [
      "What can go: most household junk, furniture, appliances, and debris can be hauled. Hazardous materials like paint, chemicals, and certain batteries usually can't — ask first.",
      "Pricing by volume: most removal services price by how much truck space your items take. Photos of the pile get you the fastest, most accurate estimate.",
      "Timing for move-outs: book ahead of your lease end or closing date — same-week and sometimes same-day service is often available, but end-of-month fills fast.",
      "Donations: usable furniture and goods can often be diverted to donation instead of the landfill. Mention it if that matters to you.",
    ],
    faqs: [
      { q: "What items can't be hauled away?", a: "Hazardous materials — wet paint, chemicals, fuels, and some batteries — typically require special disposal. Your specialist will tell you what they can take and where the rest can go." },
      { q: "How is junk removal priced?", a: "Usually by volume — a quarter truck, half truck, full truck — with labor included. Sending photos when you request a connection gets you a tighter estimate." },
      { q: "Do you offer move-out cleanouts for rentals?", a: "Yes. Landlords, property managers, and real estate agents use cleanout services between tenants regularly. Note the deadline in your request so timing is clear." },
      { q: "Can you remove just one large item, like a sofa or fridge?", a: "Single-item pickups are common and priced as a minimum load. It's an easy way to get rid of something the weekly trash won't take." },
    ],
    relatedServices: ["landscaping-and-lawn-maintenance", "tree-removal-and-land-clearing", "drywall"],
    relatedProjects: ["demolition", "complete-home-remodels"],
    guideTags: ["Hiring Tips"],
  },
];
